import { useAuth, useSignIn, useSignUp } from "@clerk/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { AuthScreen } from "@/components/auth/auth-screen";
import { PrimaryButton } from "@/components/auth/primary-button";
import { TextField } from "@/components/auth/text-field";
import { TextLink } from "@/components/auth/text-link";
import {
  authPath,
  emailLooksComplete,
  hasEnterpriseSsoFactor,
  isEmployeeEmail,
  isEnterpriseSsoError,
  redirectAfterAuth,
} from "@/lib/auth-helpers";
import { clerkFailureMessage } from "@/lib/clerk-errors";
import {
  emailAddressFromClerkTicket,
  releaseClerkTicket,
  shareInflight,
  takeClerkTicketFromLocation,
} from "@/lib/clerk-username";
import { useSignUpMode } from "@/lib/use-public-sign-up";

import { continueFromEmail, redeemClerkTicket } from "./email-flow";

const SSO_LOOKUP_MS = 350;

const SignInEmailPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { isLoaded, isSignedIn } = useAuth();
  const { fetchStatus, signIn } = useSignIn();
  const { signUp } = useSignUp();
  const signUpMode = useSignUpMode();
  const signupLabel =
    signUpMode === "waitlist" ? "Join the waitlist" : "Create an account";
  const showSignup = signUpMode === "public" || signUpMode === "waitlist";
  const [ticket] = useState(takeClerkTicketFromLocation);
  const [ticketSettled, setTicketSettled] = useState(!ticket.token);
  const [email, setEmail] = useState(
    () =>
      params.get("email") ??
      emailAddressFromClerkTicket(ticket.token ?? "") ??
      ""
  );
  const [error, setError] = useState<string | null>(null);
  const [unknown, setUnknown] = useState(false);
  const [pending, setPending] = useState(false);
  const [clerkSso, setClerkSso] = useState(false);
  const lookupEmail = useRef("");
  const useSso = isEmployeeEmail(email) || clerkSso;

  useEffect(() => {
    if (isLoaded && isSignedIn) {
      window.location.replace(redirectAfterAuth());
    }
  }, [isLoaded, isSignedIn]);

  useEffect(() => {
    const token = ticket.token;
    const status = ticket.status;
    if (!(token && status) || isSignedIn) {
      return;
    }
    let cancelled = false;
    shareInflight(token, () =>
      redeemClerkTicket(signIn, signUp, token, status)
    ).catch((err: unknown) => {
      if (cancelled) {
        return;
      }
      releaseClerkTicket();
      const failure = err as {
        code?: string;
        longMessage?: string;
        message?: string;
      };
      setError(
        failure.code
          ? clerkFailureMessage({
              code: failure.code,
              longMessage: failure.longMessage,
              message:
                failure.message ?? "Single sign-on could not be completed.",
            })
          : (failure.message ?? "Single sign-on could not be completed.")
      );
      const ticketEmail = emailAddressFromClerkTicket(token);
      if (ticketEmail) {
        setEmail(ticketEmail);
      }
      setTicketSettled(true);
    });
    return () => {
      cancelled = true;
    };
  }, [isSignedIn, signIn, signUp, ticket.status, ticket.token]);

  const lookupSso = useCallback(
    async (value: string) => {
      const trimmed = value.trim();
      if (!emailLooksComplete(trimmed)) {
        setClerkSso(false);
        return;
      }
      if (isEmployeeEmail(trimmed)) {
        setClerkSso(true);
        return;
      }
      lookupEmail.current = trimmed;
      try {
        const { error: lookupError } = await signIn.create({
          identifier: trimmed,
        });
        if (lookupEmail.current !== trimmed) {
          return;
        }
        if (lookupError) {
          setClerkSso(
            isEnterpriseSsoError(lookupError.longMessage ?? lookupError.message)
          );
          return;
        }
        setClerkSso(hasEnterpriseSsoFactor(signIn.supportedFirstFactors));
      } catch {
        if (lookupEmail.current === trimmed) {
          setClerkSso(false);
        }
      }
    },
    [signIn]
  );

  useEffect(() => {
    if (!ticketSettled) {
      return;
    }
    const timer = window.setTimeout(() => {
      lookupSso(email).catch(() => undefined);
    }, SSO_LOOKUP_MS);
    return () => window.clearTimeout(timer);
  }, [email, lookupSso, ticketSettled]);

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setUnknown(false);
    setPending(true);
    const outcome = await continueFromEmail(signIn, email.trim(), clerkSso);
    setPending(false);
    switch (outcome.kind) {
      case "unknown":
        setUnknown(true);
        return;
      case "error":
        setError(outcome.message);
        return;
      case "sso":
        setClerkSso(true);
        return;
      case "password":
        navigate(authPath("/login/password"));
        return;
      default: {
        const unreachable: never = outcome;
        return unreachable;
      }
    }
  };

  if (isLoaded && isSignedIn) {
    return null;
  }

  if (ticket.token && !ticketSettled) {
    return (
      <AuthScreen
        subtitle="Completing single sign-on…"
        title="Signing you in"
      />
    );
  }

  return (
    <AuthScreen
      subtitle="Enter your email address to continue."
      title="Sign in"
    >
      <form className="flex flex-col gap-8" onSubmit={onSubmit}>
        <TextField
          autoComplete="username"
          error={unknown ? null : error}
          id="email"
          label="Email"
          onBlur={() => {
            lookupSso(email).catch(() => undefined);
          }}
          onChange={(event) => {
            setEmail(event.target.value);
            setUnknown(false);
            setError(null);
            if (!isEmployeeEmail(event.target.value)) {
              setClerkSso(false);
            }
          }}
          placeholder="name@company.com"
          type="email"
          value={email}
        />
        {unknown ? (
          <p className="m-0 text-error text-sm leading-[1.45]" role="alert">
            We couldn’t find an account with that email.
            {showSignup ? (
              <>
                {" "}
                <TextLink to={authPath("/signup")}>{signupLabel}</TextLink>
              </>
            ) : null}
          </p>
        ) : null}
        <PrimaryButton
          busy={pending || fetchStatus === "fetching"}
          type="submit"
        >
          {useSso ? "Continue with SSO" : "Continue"}
        </PrimaryButton>
      </form>
      {showSignup ? (
        <p className="m-0 flex flex-wrap gap-1.5 border-n-200 border-t pt-6 text-[15px] text-n-600">
          <span>New to Anomalos?</span>
          <TextLink to={authPath("/signup")}>{signupLabel}</TextLink>
        </p>
      ) : null}
    </AuthScreen>
  );
};

export default SignInEmailPage;
